import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';
import { api } from '../../api/client';
import { useAction } from '../../api/hooks';
import { useCrumbs } from '../../layout/crumbs';
import { Button } from '../../ui/Button';
import { EmptyState, QueryView } from '../../ui/feedback';
import { LoadMore, PageHeader, Tabs, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

interface Note {
  id: string;
  kind: string;
  title: string;
  body: string;
  link: string;
  createdAt: string;
  readAt: string | null;
}

/** TKT-08 inbox: access is rechecked when listing and when opening. */
export function NotificationsPage() {
  usePageTitle('Notifications');
  useCrumbs([{ label: 'Workspace' }, { label: 'Notifications' }]);
  const [params, setParams] = useSearchParams();
  const unreadOnly = params.get('filter') !== 'all';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useInfiniteQuery({
    queryKey: ['notifications', unreadOnly],
    queryFn: ({ pageParam }) =>
      api.get<{ items: Note[]; unread: number; nextCursor: string | null }>(
        `/notifications?unread=${unreadOnly}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (l) => l.nextCursor ?? undefined,
  });
  const readAll = useAction(() => api.post('/notifications/read', { ids: 'all' }), {
    success: 'All marked as read',
    invalidate: [['notifications'], ['me']],
  });
  const open = async (n: Note) => {
    try {
      const r = await api.post<{ link: string }>(`/notifications/${n.id}/open`);
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate(r.link);
    } catch {
      await qc.invalidateQueries({ queryKey: ['notifications'] });
    }
  };
  return (
    <div className="page narrow">
      <PageHeader
        title="Notifications"
        subtitle="Mentions, state changes, approval requests and delivery problems."
        actions={
          <Button variant="secondary" onClick={() => readAll.mutate(undefined)} loading={readAll.isPending}>
            Mark all as read
          </Button>
        }
      />
      <Tabs
        label="Notification filter"
        value={unreadOnly ? 'unread' : 'all'}
        onChange={(v) => setParams(v === 'all' ? { filter: 'all' } : {}, { replace: true })}
        tabs={[
          { value: 'unread', label: 'Unread', count: q.data?.pages[0]?.unread },
          { value: 'all', label: 'All' },
        ]}
      />
      <QueryView query={q}>
        {(d) => {
          const items = d.pages.flatMap((p) => p.items);
          if (!items.length)
            return (
              <EmptyState icon={Bell} title={unreadOnly ? 'You are all caught up' : 'No notifications'} />
            );
          return (
            <>
              <div className="table-wrap">
                {items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => open(n)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '10px minmax(0,1fr) auto',
                      gap: 12,
                      width: '100%',
                      textAlign: 'left',
                      padding: 16,
                      border: 'none',
                      borderBottom: '1px solid var(--color-hairline-soft)',
                      background: 'transparent',
                      cursor: 'pointer',
                      font: 'inherit',
                    }}
                  >
                    <span
                      aria-label={n.readAt ? 'Read' : 'Unread'}
                      style={{
                        width: 8,
                        height: 8,
                        marginTop: 6,
                        borderRadius: 9999,
                        background: n.readAt ? 'transparent' : 'var(--color-primary)',
                      }}
                    />
                    <span className="stack" style={{ gap: 2 }}>
                      <span className={n.readAt ? '' : 'strong'}>{n.title}</span>
                      {n.body && (
                        <span
                          className="small muted"
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {n.body}
                        </span>
                      )}
                    </span>
                    <span className="small muted">
                      <Timestamp value={n.createdAt} relative />
                    </span>
                  </button>
                ))}
              </div>
              <LoadMore
                hasMore={!!q.hasNextPage}
                onClick={() => q.fetchNextPage()}
                loading={q.isFetchingNextPage}
              />
            </>
          );
        }}
      </QueryView>
    </div>
  );
}
